import {
  AttachSquare,
  Eye,
  DocumentDownload,
  CloseCircle,
} from 'iconsax-react';
import React, { useState } from 'react';
import TitleText from './TitleText';
import InfoText, { truncateText } from './InfoText';

import { Image } from 'antd';
import { useTranslation } from 'react-i18next';

export type documentType = {
  title: string;
  sizeNumber: number;
  sizeType: string;
  documentUrl: string;
  noDownload?: boolean;
  removeDoc?: () => void;
  // Configurable icon props
  leftIcon?: React.ReactNode; // Custom left icon
  downloadIcon?: React.ReactNode; // Custom download icon
  viewIcon?: React.ReactNode; // Custom view icon
  containerClassName?: string; // Custom container styling
  iconContainerClassName?: string; // Custom icon container styling
  buttonsOrder?: 'download-first' | 'view-first'; // Button order

};

const DocumentItem = ({
  title,
  sizeNumber,
  sizeType,
  documentUrl,
  noDownload,
  removeDoc,
  leftIcon,
  downloadIcon,
  viewIcon,
  containerClassName,
  iconContainerClassName,
  buttonsOrder = 'view-first',
}: documentType) => {
  const { t } = useTranslation();

  // Modal state for preview
  const [modalOpen, setModalOpen] = useState(false);

  const defaultContainerClass = 'flex gap-2 bg-soft-background border border-light-input-border dark:border-dark-input-border dark:bg-soft-dark-background p-2 rounded-lg text-left';
  const defaultIconContainerClass = 'icon p-2 rounded-xl border border-light-input-border dark:border-dark-input-border';

  const finalContainerClass = containerClassName || defaultContainerClass;
  const finalIconContainerClass = iconContainerClassName || defaultIconContainerClass;

  const defaultLeftIcon = (
    <AttachSquare
      className="text-light-text-primary dark:text-dark-button-bg-primary"
      size={20}
    />
  );

  const defaultViewIcon = (
    <Eye
      size={20}
      className="text-light-text-primary dark:text-dark-button-bg-primary"
    />
  );

  const defaultDownloadIcon = (
    <DocumentDownload
      className="text-light-text-primary dark:text-dark-button-bg-primary"
      size={20}
    />
  );

  // Helper to determine file type from url or extension
  const getFileType = (url: string): string => {
    const ext = url.split('.').pop()?.toLowerCase();
    if (!ext) return '';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext))
      return 'image';
    if (['pdf'].includes(ext)) return 'pdf';
    return 'other';
  };

  const handleView = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    if (getFileType(documentUrl) === 'image') {
      setModalOpen(true);
    } else {
      // Open in Google Docs Viewer in a new tab
      window.open(
        `https://docs.google.com/viewer?url=${encodeURIComponent(
          documentUrl
        )}&embedded=true`,
        '_blank'
      );
    }
  };

  const handleSave = async () => {
    try {
      const response = await fetch(documentUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = title || 'document';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };


  const renderButtons = () => {
    const downloadButton = (
      <button
        key="download"
        onClick={handleSave}
        className="p-2 rounded-xl border border-light-input-border dark:border-dark-input-border hover:bg-light-hover dark:hover:bg-dark-hover"
        title="Save Document"
      >
        {downloadIcon || defaultDownloadIcon}
      </button>
    );

    const viewButton = (
      <button
        key="view"
        onClick={handleView}
        className="p-2 rounded-xl border border-light-input-border dark:border-dark-input-border hover:bg-light-hover dark:hover:bg-dark-hover"
        title="View Document"
      >
        {viewIcon || defaultViewIcon}
      </button>
    );

    if (buttonsOrder === 'download-first') {
      return [downloadButton, viewButton];
    }
    return [viewButton, downloadButton];
  };

  return (
    <>
      <div className={finalContainerClass}>
        <div className={finalIconContainerClass}>
          {leftIcon || defaultLeftIcon}
        </div>
        <div className="flex flex-col justify-between flex-1">
          <TitleText size="sm" text={truncateText(title, 20)} />
          <InfoText size="xs" text={`${sizeNumber} ${t(sizeType)} Completed`} />
        </div>
        {!!removeDoc && (
          <CloseCircle
            className="text-light-text-primary dark:text-dark-button-bg-primary cursor-pointer"
            onClick={() => removeDoc()}
          />
        )}
        {!noDownload && (
          <div className="flex gap-2">
            {renderButtons()}
          </div>
        )}
      </div>
      {/* Modal for preview */}
      {modalOpen && getFileType(documentUrl) === 'image' && (
        <Image.PreviewGroup
          preview={{
            visible: modalOpen,
            onVisibleChange: (vis) => setModalOpen(vis),
          }}
        >
          <Image src={documentUrl} alt={title} style={{ display: 'none' }} />
        </Image.PreviewGroup>
      )}
    </>
  );
};

export default DocumentItem;
